<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { useCustomerSavings } from '@/composables/cpm/customerSavings'
import { DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import type { ICustomerSavingsRecord } from '@/types/cpm'
import type { ICustomerSavingsFilters } from '@/types/cpm/filters'
import { formatCurrency } from '@/util/formatters'
import { DateTime } from 'luxon'

const props = defineProps<{
  filters: ICustomerSavingsFilters
  isAdmin: boolean
}>()

const emit = defineEmits<{
  (e: 'update-filters', value: Partial<ICustomerSavingsFilters>): void
  (e: 'edit', record: ICustomerSavingsRecord): void
}>()

// 12 = one record per month, so a full year fits on a single page.
const PAGE_SIZES = [12, 24, 50, 100]

const { getCustomerSavingsQuery, deleteCustomerSavingsMutation } = useCustomerSavings()

const { data: savingsData, isLoading } = getCustomerSavingsQuery(props.filters)

// ── Formatters (all numeric fields arrive as DRF Decimal strings) ──
const formatPeriod = (row: ICustomerSavingsRecord) => {
  return DateTime.fromObject({ year: row.year, month: row.month }).toFormat('MMMM yyyy')
}

const formatKwh = (_row: ICustomerSavingsRecord, _col: unknown, value: string) => {
  const parsed = parseFloat(value)
  if (Number.isNaN(parsed)) return '-'
  return parsed.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const formatRate = (_row: ICustomerSavingsRecord, _col: unknown, value: string) => {
  const num = parseFloat(value)
  if (Number.isNaN(num)) return '-'
  return formatCurrency(num, 2)
}

const formatUsd = (_row: ICustomerSavingsRecord, _col: unknown, value: string) => {
  const num = parseFloat(value)
  if (Number.isNaN(num)) return '-'
  return formatCurrency(num)
}

const confirmDelete = (row: ICustomerSavingsRecord) => {
  DeleteConfirmationModal(
    'Delete Savings Record',
    'Are you sure you want to delete this savings record? This action cannot be undone.',
    row.id,
    deleteCustomerSavingsMutation.mutateAsync
  )
}

const updatePage = (page: number) => emit('update-filters', { page })
const updatePageSize = (per_page: number) => emit('update-filters', { per_page })
</script>

<template>
  <div class="flex flex-col flex-grow-1">
    <el-table v-loading="isLoading" :data="savingsData?.data ?? []" stripe style="width: 100%">
      <template #empty>
        <el-empty description="Select a customer and site, or add a new record." />
      </template>

      <el-table-column label="Month and Year" prop="year" :formatter="formatPeriod" min-width="140" />
      <el-table-column
        label="Average hourly load, kWh"
        prop="average_hourly_consumption_kwh"
        :formatter="formatKwh"
        align="right"
        min-width="120"
      />
      <el-table-column
        label="CP hour load, KWh"
        prop="cp_hour_load_kwh"
        :formatter="formatKwh"
        align="right"
        min-width="130"
      />
      <el-table-column
        label="CP schedules rate, $/KWh"
        prop="cp_schedule_rate_usd_per_kwh"
        :formatter="formatRate"
        align="right"
        min-width="160"
      />
      <el-table-column
        label="Total savings, $"
        prop="total_savings_usd"
        :formatter="formatUsd"
        align="right"
        min-width="120"
      />
      <el-table-column
        label="CP management fee, $"
        prop="cp_management_fee_usd"
        :formatter="formatUsd"
        align="right"
        min-width="140"
      />

      <el-table-column v-if="isAdmin" width="110" fixed="right" align="center">
        <template #default="{ row }">
          <el-button title="Edit" link @click="emit('edit', row as ICustomerSavingsRecord)">
            <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
          </el-button>
          <el-button title="Delete" link @click="confirmDelete(row as ICustomerSavingsRecord)">
            <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="savingsData && savingsData.total_pages > 1"
      class="flex justify-end mt-[var(--spacing-spacing-md)]"
      :current-page="props.filters.page"
      :page-size="props.filters.per_page"
      :page-sizes="PAGE_SIZES"
      :page-count="savingsData?.total_pages"
      :total="savingsData?.total_records"
      :disabled="isLoading"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
      @current-change="updatePage"
      @size-change="updatePageSize"
    />
  </div>
</template>
